import type { Theme, SxProps } from '@mui/material/styles';

import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { RouterLink } from '../../routes/components';

// ----------------------------------------------------------------------

export type BreadcrumbsLinkProps = React.ComponentProps<'div'> & {
  name?: string;
  href?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  sx?: SxProps<Theme>;
};

export function BreadcrumbsLink({ href, icon, name, disabled, ...other }: BreadcrumbsLinkProps) {
  const renderContent = () => (
    <ItemRoot disabled={disabled} {...other}>
      {icon && <ItemIcon>{icon}</ItemIcon>}
      {name}
    </ItemRoot>
  );

  if (!href) return renderContent();

  // Only force reload for:
  // - explicit ?reload=true flag
  // - external URLs (different origin like https://google.com)
  const isReloadFlag = href.includes('reload=true');
  const isExternal = /^https?:\/\//.test(href) && !href.startsWith(window.location.origin);

  const forceReload = isReloadFlag || isExternal;

  if (forceReload) {
    // Render plain anchor (<a href="...">) — will cause browser full reload
    return (
      <Link
        href={href}
        color="inherit"
        sx={{
          display: 'inline-flex',
          ...(disabled && { pointerEvents: 'none' }),
        }}
      >
        {renderContent()}
      </Link>
    );
  }

  // Default: use RouterLink for SPA navigation (all relative paths)
  return (
    <Link
      component={RouterLink}
      href={href} // RouterLink expects href and maps to `to`
      color="inherit"
      sx={{
        display: 'inline-flex',
        ...(disabled && { pointerEvents: 'none' }),
      }}
    >
      {renderContent()}
    </Link>
  );
}

// ----------------------------------------------------------------------

const ItemRoot = styled('div', {
  shouldForwardProp: (prop: string) => !['disabled', 'sx'].includes(prop),
})<Pick<BreadcrumbsLinkProps, 'disabled'>>(({ disabled, theme }) => ({
  ...theme.typography.body2,
  alignItems: 'center',
  gap: theme.spacing(1),
  display: 'inline-flex',
  color: theme.vars.palette.text.primary,
  ...(disabled && {
    cursor: 'default',
    pointerEvents: 'none',
    color: theme.vars.palette.text.disabled,
  }),
}));

const ItemIcon = styled('span')(() => ({
  display: 'inherit',
  '& > :first-of-type:not(style):not(:first-of-type ~ *), & > style + *': {
    width: 20,
    height: 20,
  },
}));
